/**
 * Widget realtime client — Phase 1B Slice 4.
 *
 * Wraps a socket-style {@link WidgetTransport} on the `/widget` namespace.
 * The transport interface is intentionally minimal so we can:
 *   - unit-test the client with a fake transport (no real socket needed);
 *   - swap socket.io-client in later without changing call sites.
 *
 * Responsibilities:
 *   - Connect with the JWT issued by `POST /v1/widget/session`.
 *   - Reconnect with exponential backoff on disconnect.
 *   - Refresh the JWT 5 minutes before expiry and reconnect transparently.
 *   - Emit `message:send` and surface `message:new` and `typing`.
 *   - Track a `ConnectionState` so the panel can render status badges.
 *
 * The client is intentionally framework-agnostic — it does not touch the
 * DOM. Callers (typically `main.ts`) feed events into `mountPanel`.
 */

import type { ChatMessageDto, Iso8601 } from '@prochat/shared'
import type { WidgetTheme } from './theme'

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'closed'
  | 'error'

export interface PageContext {
  url: string
  title?: string
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  userAgent?: string
  device?: string
  timestamp?: string
}

export interface WidgetSession {
  /** Opaque widget JWT. Will be sent as `auth.token` on connect. */
  token: string
  /** Absolute expiry. The client refreshes 5 minutes before this. */
  expiresAt: Iso8601
  visitorId: string
  conversationId: string | null
  visitorToken: string
  /** Widget branding configuration from the API. */
  theme: WidgetTheme
  /** Optional title for the widget header. */
  title?: string | null
  /** Optional greeting message. */
  welcomeMessage?: string | null
  /** Optional starter questions shown before freeform chat begins. */
  quickQuestions: string[]
  /**
   * Whether the server created this conversation, or resumed an existing one.
   *
   * Authoritative for "should the greeting and starter questions show?". Do
   * not substitute `messages.length === 0`: a returning visitor who opened the
   * chat and closed it without typing has zero messages in an *existing*
   * conversation, and guessing from the count replays both on every reopen.
   * Absent (older API) means fall back to the message count.
   */
  isNewConversation?: boolean
  /** App name (shown as fallback title). */
  appName: string
  /**
   * Recent conversation history from the session response (oldest → newest).
   * Empty for brand-new conversations. Used to seed the panel on reopen.
   */
  messages: ChatMessageDto[]
  /** Resolver the client calls when it needs a fresh JWT. */
  refresh(): Promise<WidgetSession>
}

/**
 * Minimal transport interface — matches the slice of socket.io-client's
 * API we actually use. A socket.io wrapper will implement this.
 */
export interface WidgetTransport {
  connect(opts: { token: string }): Promise<void>
  disconnect(): void
  emit(event: string, payload: unknown): void
  /**
   * Emit and wait for the server-side acknowledgement (socket.io ack
   * callback). Rejects on timeout or when no socket is connected.
   */
  emitWithAck(event: string, payload: unknown, timeoutMs?: number): Promise<unknown>
  on(event: string, handler: (payload: unknown) => void): void
  off(event: string, handler: (payload: unknown) => void): void
  onState(handler: (state: 'connecting' | 'connected' | 'disconnected' | 'error') => void): void
}

/** Outcome of a `message:send`, resolved from the gateway ack. */
export type SendAck =
  | { ok: true; message?: ChatMessageDto }
  | { ok: false; reason: string }

export interface SendResult {
  /** Client-generated id — available synchronously for optimistic UI. */
  externalMessageId: string
  /** Resolves once the server acks (or the ack times out). Never rejects. */
  ack: Promise<SendAck>
}

export interface WidgetClientOptions {
  apiBaseUrl: string
  transport: WidgetTransport
  session: WidgetSession
  /** Override the auto-refresh margin (default 5 minutes). */
  refreshMarginMs?: number
  /** Override reconnect backoff settings. */
  backoff?: { initialMs: number; maxMs: number; factor: number }
  /** Optional clock — exposed for tests. */
  now?: () => number
}

export interface WidgetClient {
  /** Current connection state. */
  readonly state: ConnectionState
  /** Begin connecting (or reconnect if a refresh already fired). Idempotent. */
  connect(): Promise<void>
  /** Subscribe to connection state changes. Returns an unsubscribe fn. */
  onState(handler: (state: ConnectionState) => void): () => void
  /**
   * Send a visitor message over the realtime channel. Returns the
   * externalMessageId synchronously (for optimistic rendering) plus an
   * `ack` promise that resolves with the server's verdict.
   */
  sendMessage(
    content: string,
    opts?: { contentType?: ChatMessageDto['contentType'] },
  ): SendResult
  /** Send the visitor's current page context over realtime. */
  trackPageview(context: PageContext): { ok: boolean }
  /** Subscribe to inbound messages from agent/bot. Returns an unsubscribe fn. */
  onMessage(handler: (message: ChatMessageDto) => void): () => void
  /** Subscribe to typing indicators. Returns an unsubscribe fn. */
  onTyping(
    handler: (payload: { senderType: 'agent' | 'visitor'; isTyping: boolean }) => void,
  ): () => void
  /** Tear down timers + transport listeners. */
  destroy(): void
}

const DEFAULT_REFRESH_MARGIN_MS = 5 * 60 * 1000
const DEFAULT_BACKOFF = { initialMs: 500, maxMs: 8_000, factor: 2 }

/**
 * Construct a widget realtime client.
 *
 * The client does NOT auto-connect on construction — call `client.connect()`
 * (or the convenience {@link startClient} wrapper) to begin. This split
 * keeps the class unit-testable without an actual socket.
 */
export function createWidgetClient(opts: WidgetClientOptions): WidgetClient {
  const now = opts.now ?? (() => Date.now())
  const refreshMargin = opts.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS
  const backoff = opts.backoff ?? DEFAULT_BACKOFF
  const transport = opts.transport

  let state: ConnectionState = 'idle'
  const stateHandlers = new Set<(s: ConnectionState) => void>()
  const messageHandlers = new Set<(m: ChatMessageDto) => void>()
  const typingHandlers = new Set<(p: {
    senderType: 'agent' | 'visitor'
    isTyping: boolean
  }) => void>()

  let session: WidgetSession = opts.session
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let recoveryTimer: ReturnType<typeof setTimeout> | null = null
  let destroyed = false
  let reconnectAttempts = 0

  // Transport listeners (kept as named refs so off() can detach them).
  const onTransportMessage = (raw: unknown) => {
    const m = raw as ChatMessageDto
    if (m && typeof m === 'object' && typeof m.id === 'string') {
      for (const h of messageHandlers) h(m)
    }
  }
  const onTransportTyping = (raw: unknown) => {
    const t = raw as { senderType?: string; isTyping?: boolean }
    if (
      t &&
      (t.senderType === 'agent' || t.senderType === 'visitor') &&
      typeof t.isTyping === 'boolean'
    ) {
      for (const h of typingHandlers) h({ senderType: t.senderType, isTyping: t.isTyping })
    }
  }
  const onTransportState = (s: 'connecting' | 'connected' | 'disconnected' | 'error') => {
    if (destroyed) return
    if (s === 'connected') {
      reconnectAttempts = 0
      clearRecoveryTimer()
      setState('connected')
      scheduleRefresh()
    } else if (s === 'connecting') {
      setState(reconnectAttempts > 0 ? 'reconnecting' : 'connecting')
    } else if (s === 'disconnected') {
      setState('reconnecting')
      scheduleRecovery()
    } else if (s === 'error') {
      // Handshake rejected (e.g. expired JWT after the tab slept) or the
      // transport gave up. The real socket.io transport resolves connect()
      // before the handshake, so this is the only signal we get — recover
      // here instead of sitting in 'error' forever.
      setState('error')
      scheduleRecovery()
    }
  }

  transport.on('message:new', onTransportMessage)
  transport.on('typing', onTransportTyping)
  transport.onState(onTransportState)

  function setState(next: ConnectionState) {
    if (state === next) return
    state = next
    // Skip notifications after destroy, but still update internal state so
    // the public `state` getter reflects the closed transition.
    if (destroyed) return
    for (const h of stateHandlers) h(next)
  }

  function clearRefreshTimer() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  function clearRecoveryTimer() {
    if (recoveryTimer) {
      clearTimeout(recoveryTimer)
      recoveryTimer = null
    }
  }

  /** True when the current JWT is expired or inside the refresh margin. */
  function tokenNeedsRefresh(): boolean {
    const expiresAt = new Date(session.expiresAt).getTime()
    if (Number.isNaN(expiresAt)) return false
    return now() >= expiresAt - refreshMargin
  }

  /**
   * Schedule a single recovery attempt with exponential backoff. Used for
   * both transport-level failures ('error'/'disconnected' states) and
   * connect() rejections. If the token is expired (e.g. the tab slept past
   * expiresAt and the handshake now fails with "widget: invalid token"),
   * recovery fetches a fresh session before reconnecting; otherwise it
   * reconnects with the current token.
   */
  function scheduleRecovery() {
    if (destroyed || recoveryTimer) return
    reconnectAttempts += 1
    const delay = Math.min(
      backoff.maxMs,
      backoff.initialMs * Math.pow(backoff.factor, reconnectAttempts - 1),
    )
    recoveryTimer = setTimeout(() => {
      recoveryTimer = null
      if (destroyed || state === 'connected') return
      if (tokenNeedsRefresh()) {
        void doRefresh()
      } else {
        // Swallow rejection — state transitions are the source of truth.
        connect().catch(() => {})
      }
    }, delay)
  }

  function scheduleRefresh() {
    if (destroyed) return
    clearRefreshTimer()
    const expiresAt = new Date(session.expiresAt).getTime()
    const msUntilRefresh = Math.max(0, expiresAt - now() - refreshMargin)
    refreshTimer = setTimeout(() => {
      void doRefresh()
    }, msUntilRefresh)
  }

  async function doRefresh() {
    if (destroyed) return
    try {
      const next = await session.refresh()
      session = next
      // Reconnect with the fresh token transparently.
      transport.disconnect()
      await connect()
    } catch {
      // Retry with a small delay; session.refresh() may be a network call.
      setTimeout(() => void doRefresh(), backoff.initialMs)
    }
  }

  async function connect(): Promise<void> {
    if (destroyed) return
    setState(reconnectAttempts > 0 ? 'reconnecting' : 'connecting')
    try {
      await transport.connect({ token: session.token })
      if (destroyed) return
      reconnectAttempts = 0
      clearRecoveryTimer()
      setState('connected')
      scheduleRefresh()
    } catch {
      // Resilient design: do NOT throw out of connect(). The first failure
      // flips us to 'reconnecting' and schedules a retry with exponential
      // backoff (refreshing the session first when the token is stale).
      // Callers observe state transitions via onState() instead of a
      // promise rejection — `startClient()` therefore never rejects on
      // a single transient failure.
      if (destroyed) return
      scheduleRecovery()
    }
  }

  function sendMessage(
    content: string,
    sendOpts: { contentType?: ChatMessageDto['contentType'] } = {},
  ): SendResult {
    const externalMessageId = randomId()
    const ack = transport
      .emitWithAck('message:send', {
        content,
        contentType: sendOpts.contentType ?? 'text',
        externalMessageId,
      })
      .then((raw): SendAck => {
        const r = raw as { ok?: unknown; reason?: unknown; message?: unknown }
        if (r && typeof r === 'object' && r.ok === true) {
          const m = r.message as ChatMessageDto | undefined
          return m && typeof m === 'object' && typeof m.id === 'string'
            ? { ok: true, message: m }
            : { ok: true }
        }
        const reason =
          r && typeof r === 'object' && typeof r.reason === 'string' ? r.reason : 'send-failed'
        return { ok: false, reason }
      })
      .catch((err: unknown): SendAck => ({
        ok: false,
        reason: err instanceof Error ? err.message : 'send-failed',
      }))
    return { externalMessageId, ack }
  }

  function trackPageview(context: PageContext): { ok: boolean } {
    if (!context.url.trim()) return { ok: false }
    transport.emit('visitor:pageview', context)
    return { ok: true }
  }

  return {
    get state() {
      return state
    },
    connect,
    onState(handler) {
      stateHandlers.add(handler)
      handler(state)
      return () => stateHandlers.delete(handler)
    },
    sendMessage,
    trackPageview,
    onMessage(handler) {
      messageHandlers.add(handler)
      return () => messageHandlers.delete(handler)
    },
    onTyping(handler) {
      typingHandlers.add(handler)
      return () => typingHandlers.delete(handler)
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      clearRefreshTimer()
      clearRecoveryTimer()
      transport.off('message:new', onTransportMessage)
      transport.off('typing', onTransportTyping)
      transport.disconnect()
      // Flip the state synchronously BEFORE clearing subscribers. setState()
      // short-circuits when destroyed, so we write the closure variable
      // directly and notify any subscribers still listening.
      state = 'closed'
      for (const h of stateHandlers) h('closed')
      stateHandlers.clear()
      messageHandlers.clear()
      typingHandlers.clear()
    },
  }
}

/**
 * Kick off the initial connect for a freshly-created client. Returns the
 * same client for ergonomic chaining: `startClient(createWidgetClient(...))`.
 */
export async function startClient(client: WidgetClient): Promise<WidgetClient> {
  await client.connect()
  return client
}

/** Random ID without depending on `crypto.randomUUID` in older jsdom. */
function randomId(): string {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  let s = ''
  for (let i = 0; i < bytes.length; i++) {
    s += (bytes[i] ?? 0).toString(16).padStart(2, '0')
  }
  return s
}